<script setup>
import Reminder from '@/components/Reminder.vue';
import { computed, ref, onMounted } from 'vue';
import { usePerformanceStore } from '@/stores/performance';
import { useUserStore } from '@/stores/user';
import userAvatar from '@/components/userAvatar.vue';
import BarNew from '@/components/Bar_new.vue';

const performanceStore = usePerformanceStore();
const userStore = useUserStore();

// Immer die Kampagnen-Daten der aktuellen Auswahl verwenden
const campaignPerf = computed(() => performanceStore.performance?.campaign || null);
const statusList = computed(() => campaignPerf.value?.status?.statusList || []);
const currentStatusName = computed(() => campaignPerf.value?.status?.currentStatus || '');
const plannedStatusName = computed(() => campaignPerf.value?.status?.plannedStatus || '');

// Build normalized list (dedupe by name, keep first occurrence) and insert special "starSeller" award at top
const rows = computed(() => {
  const raw = statusList.value || [];
  const seen = new Set();
  const base = [];
  const perf = campaignPerf.value;
  const pct = (cur, goal) => {
    if (!goal || goal <= 0) return 0;
    return (cur / goal) * 100;
  };
  for (const s of raw) {
    if (!s?.name) continue;
    if (seen.has(s.name)) continue; // remove duplicate like "Professional Silber"
    seen.add(s.name);
    const planned = s.plannedValues || {};
    const curSales = perf?.salesEuro?.current || 0;
    const curNewEuro = perf?.salesEuroNewCustomers?.current || 0;
    const curNewCount = perf?.salesNumberNewCustomers?.current || 0;
    const pctSales = pct(curSales, planned.salesEuro);
    const pctNewEuro = pct(curNewEuro, planned.salesEuroNewCustomers);
    const pctNewCount = pct(curNewCount, planned.salesNumberNewCustomers);
    // Status gilt als erreicht, wenn Gesamt UND (Neu-Kd. € ODER Neu-Kd. #) jeweils >= 100% sind
    const achievedByMetrics = pctSales >= 100 && (pctNewEuro >= 100 || pctNewCount >= 100);
    base.push({
      name: s.name,
      key: s.name.toLowerCase().replace(/\s+/g, '-'),
      plannedValues: planned,
      commission: s.commission ?? 0,
      commissionOnTop: s.commissionOnTop ?? 0,
      levelValue: s.levelValue ?? 0,
      achieved: !!s.achieved || achievedByMetrics,
      achievedLatest: !!s.achievedLatest,
      isCurrent: s.name === currentStatusName.value,
      isPlanned: s.name === plannedStatusName.value,
    });
  }
  // Canonical ordering per requirement (top to bottom after special award):
  // elite-gold, elite-silber, professional-gold, professional-silber, advanced-gold, advanced-silber, basic
  const ladderOrder = [
    'elite gold',
    'elite silber',
    'professional gold',
    'professional silber',
    'advanced gold',
    'advanced silber',
    'basic'
  ];
  // Helper: normalize variants & typos (e.g. "elilte", English "silver")
  const canonical = (n) => {
    if (!n) return '';
    let x = n.toLowerCase().trim();
    x = x.replace(/elilte/g, 'elite'); // typo tolerance
    x = x.replace(/silver/g, 'silber');
    // unify multiple spaces / dashes
    x = x.replace(/\s+/g, ' ');
    return x;
  };
  base.sort((a, b) => {
    const ai = ladderOrder.indexOf(canonical(a.name));
    const bi = ladderOrder.indexOf(canonical(b.name));
    if (ai === -1 && bi === -1) return 0; // neither known => keep relative
    if (ai === -1) return 1; // unknowns sink
    if (bi === -1) return -1;
    return ai - bi;
  });
  const currentIndex = base.findIndex(r => r.isCurrent);
  let ascending = true;
  for (let i = 1; i < base.length; i++) {
    const prev = base[i - 1].plannedValues?.salesEuro || 0;
    const cur = base[i].plannedValues?.salesEuro || 0;
    if (cur < prev) { ascending = false; break; }
  }
  const classified = base.map((r, idx) => {
    let isPast = false;
    let isFuture = false;
    if (currentIndex !== -1) {
      if (ascending) {
        isPast = (idx < currentIndex || (idx === currentIndex && r.achieved)) && r.achieved;
        isFuture = idx > currentIndex && !r.achieved;
      } else {
        isPast = (idx > currentIndex || (idx === currentIndex && r.achieved)) && r.achieved;
        isFuture = idx < currentIndex && !r.achieved;
      }
    }
    return { ...r, isPast, isFuture };
  });
  // Insert starSeller award at top (not part of ladder progression)
  const starSeller = {
    name: 'Star Seller',
    key: 'star-seller',
    plannedValues: {},
    commission: 0,
    commissionOnTop: 0,
    levelValue: 0,
    achieved: false,
    achievedLatest: false,
    isCurrent: false,
    isPlanned: false,
    isPast: false,
    isFuture: false,
    isSpecial: true,
    awardNote: 'Jährliche Auszeichnung – höchster Gesamtumsatz'
  };
  return [starSeller, ...classified];
});
// Nächster Rang: immer der direkte Nachbar in Richtung geplanten Status
const nextStatus = computed(() => {
  const ladder = rows.value.filter(r => !r.isSpecial);
  const currentIdx = ladder.findIndex(r => r.isCurrent);
  if (currentIdx === -1) return null;
  const plannedIdx = ladder.findIndex(r => r.isPlanned);
  if (plannedIdx !== -1 && plannedIdx !== currentIdx) {
    const step = plannedIdx > currentIdx ? 1 : -1;
    const nextIdx = currentIdx + step;
    return ladder[nextIdx] || null;
  }
  if (currentIdx < ladder.length - 1) {
    return ladder[currentIdx + 1];
  }
  return null;
});

// Planned status object (may be null if not present)
const plannedStatus = computed(() => rows.value.find(r => r.isPlanned) || null);

function remainingMetric(goal, current) {
  if (!goal || goal === 0) return 0;
  const rem = goal - current;
  return rem > 0 ? rem : 0;
}

const metricsCurrent = computed(() => ({
  salesEuro: campaignPerf.value?.salesEuro?.current || 0,
  salesEuroNew: campaignPerf.value?.salesEuroNewCustomers?.current || 0,
  salesNoNew: campaignPerf.value?.salesNumberNewCustomers?.current || 0,
}));

// Gemeinsame Verkaufszeit-Progressbasis (0-1) wie im Performance-Widget
const progressExpired = computed(() => campaignPerf.value?.progress?.expired ?? 0);
const progressFraction = computed(() => {
  const raw = Number.isFinite(progressExpired.value) ? progressExpired.value : 0;
  const rounded = Math.round(raw);
  const clamped = Math.min(Math.max(rounded, 0), 100);
  return clamped / 100;
});

// Zielstatus-Helfer wie im Performance-Widget
const areaManagerId = computed(() => {
  if (userStore.currentSelection.scope !== 'salesTeam') return null;
  const mgr = userStore.colleagues?.find(c => c.roles?.some(r => r.name === 'SMA:salesTeamManager' && r.team?.id === userStore.currentSelection.salesTeam.id));
  return mgr?.id || null;
});

const targetStatusIsAchieved = computed(() => {
  return performanceStore.performance['campaign']?.status?.achieved === true;
});

const compactMode = ref(false);
const hideAchieved = ref(false);
const hideFuture = ref(false);
const focusPath = ref(false);

const pathSet = computed(() => {
  if (!focusPath.value) return new Set();
  const currentIdx = rows.value.findIndex(r => r.isCurrent);
  const plannedIdx = rows.value.findIndex(r => r.isPlanned);
  if (currentIdx === -1 || plannedIdx === -1) return new Set();
  const [start, end] = currentIdx < plannedIdx ? [currentIdx, plannedIdx] : [plannedIdx, currentIdx];
  return new Set(rows.value.slice(start, end + 1).map(r => r.key));
});

const filteredRows = computed(() => {
  let base = rows.value;
  if (hideAchieved.value) base = base.filter(r => !r.isPast || r.isSpecial);
  if (hideFuture.value) base = base.filter(r => !r.isFuture || r.isSpecial);
  if (focusPath.value && pathSet.value.size) base = base.filter(r => r.isSpecial || pathSet.value.has(r.key));
  return base;
});

function toggleFocus() { focusPath.value = !focusPath.value; }

function formatEuro(v) { if (!v) return '0€'; return v.toLocaleString('de-DE') + '€'; }

// Beim Aufruf der Journey sicherstellen, dass
// 1) der Performance-Store Kampagnendaten hat
// 2) der Anzeige-Modus auf "campaign" steht, damit Bar-Komponente
//    ihre achieved/positive-Logik ebenfalls auf Kampagnendaten basiert
onMounted(() => {
  if (!performanceStore.performance || !performanceStore.performance.campaign) {
    performanceStore.refresh().catch(() => { });
  }
  userStore.selectDisplayStatus('performance', 'campaign');
});
</script>

<template>
  <div class="performance-ranks singlepage-wrapper" :class="{ 'compact': compactMode, 'focus-active': focusPath }">
    <div class="page-inner frame-width">
      <div class="top-actions">
        <router-link to="/dashboard" class="back-btn">← Zurück zum Dashboard</router-link>
      </div>
      <section class="hero">
        <div class="hero-main">
          <div class="hero-titles">
            <h1>Performance Journey</h1>
            <p class="subtitle">Dein aktueller Status & Weg zu den nächsten Zielen</p>
          </div>
          <div class="hero-stats">
            <div class="stat">
              <span class="label">Aktuell</span>
              <span class="value accent">{{ currentStatusName || '—' }}</span>
            </div>
            <div class="stat" v-if="plannedStatusName">
              <span class="label">Geplant</span>
              <span class="value planned">{{ plannedStatusName }}</span>
            </div>
            <div class="stat" v-if="nextStatus">
              <span class="label">Nächster</span>
              <span class="value next">{{ nextStatus.name }}</span>
            </div>
            <div class="stat" v-if="nextStatus?.commission">
              <span class="label">Provision nächster</span>
              <span class="value prov">{{ formatEuro(nextStatus.commission) }}</span>
            </div>
          </div>
          <div class="toggle-group">
            <button type="button" :class="['pill', { active: !compactMode }]" @click="compactMode = false">Normal</button>
            <button type="button" :class="['pill', { active: compactMode }]" @click="compactMode = true">Kompakt</button>
            <button type="button" :class="['pill', { active: hideAchieved }]"
              @click="hideAchieved = !hideAchieved">Erreichte aus</button>
            <button type="button" :class="['pill', { active: hideFuture }]" @click="hideFuture = !hideFuture">Zukünftige
              aus</button>
            <button type="button" :class="['pill', { active: focusPath }]" @click="toggleFocus">Fokus Pfad</button>
          </div>
        </div>
        <div class="hero-secondary" v-if="nextStatus">
          <h3>Verbleibend bis "{{ nextStatus.name }}"</h3>
          <ul class="delta-inline">
            <li v-if="nextStatus.plannedValues.salesEuro">
              <span>Gesamt</span>
              <strong>{{ remainingMetric(nextStatus.plannedValues.salesEuro,
                metricsCurrent.salesEuro).toLocaleString('de-DE') }}€</strong>
            </li>
            <li v-if="nextStatus.plannedValues.salesEuroNewCustomers">
              <span>Neu-Kd. €</span>
              <strong>{{ remainingMetric(nextStatus.plannedValues.salesEuroNewCustomers,
                metricsCurrent.salesEuroNew).toLocaleString('de-DE') }}€</strong>
            </li>
            <li v-if="nextStatus.plannedValues.salesNumberNewCustomers">
              <span>Neu-Kd. #</span>
              <strong>{{ remainingMetric(nextStatus.plannedValues.salesNumberNewCustomers,
                metricsCurrent.salesNoNew).toLocaleString('de-DE') }}</strong>
            </li>
          </ul>
        </div>
      </section>

      <div v-if="!rows.length" class="empty">Keine Daten – bitte Performance zuerst laden.</div>
      <div v-else class="layout-grid">
        <div v-if="userStore.currentSelection.performance.display === 'campaign'" class="target-status-grid-row">
          <h3 class="underlined">Zielstatus</h3>
          <div :class="['target-status-wrapper', { 'targetStatusIsAchieved': targetStatusIsAchieved }]">
            <div class="status start" v-if="userStore.currentSelection.scope === 'salesAgent'">
              <userAvatar
                v-if="userStore.currentSelection.scope === 'salesAgent'"
                layout="column" :size="48" :isPerformance="true" :salesAgentId="userStore.currentSelection?.salesAgent?.id" />
              <userAvatar
                v-else-if="userStore.currentSelection.scope === 'salesTeam' && areaManagerId"
                layout="column" :size="48" :isPerformance="true" :idUser="areaManagerId" />
              <userAvatar
                v-else
                layout="column" :size="48" :isPerformance="true" :idUser="userStore.id" :isUserAvatar="true" />
            </div>
            <div v-if="userStore.currentSelection.scope === 'salesAgent'" class="status target">
              <div v-if="targetStatusIsAchieved" class="icon-wrapper"><img src="@/assets/imgs/icons/circle-check-solid.svg"></div>
              <div v-else class="icon-wrapper"><img src="@/assets/imgs/icons/circle-xmark-solid.svg"></div>
            </div>
            <div class="status goal">
              <userAvatar
                v-if="userStore.currentSelection.scope === 'salesAgent'"
                layout="column" :size="48" :isPerformance="true" :showPoints="false" :showPlannedStatus="true" :salesAgentId="userStore.currentSelection?.salesAgent?.id" />
              <userAvatar
                v-else-if="userStore.currentSelection.scope === 'salesTeam' && areaManagerId"
                layout="column" :size="48" :isPerformance="true" :showPoints="false" :showPlannedStatus="true" :idUser="areaManagerId" />
              <userAvatar
                v-else
                layout="column" :size="48" :isPerformance="true" :showPoints="false" :showPlannedStatus="true" :idUser="userStore.id" :isUserAvatar="true" />
            </div>
          </div>
        </div>
        <div class="list-col">
          <div class="timeline-spine"></div>
          <div class="rows-wrapper">
            <div v-for="r in filteredRows" :key="r.key"
              :class="['rank-row', { 'achieved': r.achieved, 'current': r.isCurrent, 'past': r.isPast, 'future': r.isFuture, 'planned': r.isPlanned, 'path-in': pathSet.has(r.key), 'star-seller': r.isSpecial }]">
              <div class="avatar-col">
                <userAvatar :statusOverride="{ name: r.name, class: r.key.replace('-silber', '-silver') }" :size="64"
                  layout="column" hideText="true" />
                <div class="labels">
                  <p class="rank-name">{{ r.name }}</p>
                  <p class="tag tag-current" v-if="r.isCurrent">Aktuell</p>
                  <p class="tag" v-else-if="r.achievedLatest">Neu</p>
                  <p class="tag past" v-else-if="r.isPast">Erreicht</p>
                  <p class="tag planned planned-target" v-else-if="r.isPlanned">Geplant ?</p>
                  <p class="tag planned future-tag" v-else-if="r.isFuture">Zukünftig</p>
                  <p class="tag award" v-else-if="r.isSpecial">Award</p>
                  <p class="tag" v-else>Nicht erreicht</p>
                </div>
              </div>
              <div class="bars-col">
                <div class="bars-wrapper" v-if="!r.isSpecial">
                  <!-- Globale Verkaufszeit-Markierung wie im Performance-Widget -->
                  <span class="target-line" :style="{ width: `calc((100% - 24px) * ${progressFraction})` }"
                    title="Verstrichene Zeit"></span>

                  <!-- Verkaufszeit-Bar -->
                  <BarNew v-if="campaignPerf && campaignPerf.progress" class="selling-time" mode="performanceNormal"
                    title="Verkaufszeit" :currentValue="campaignPerf.progress.current"
                    :goalValue="campaignPerf.progress.planned" />

                  <!-- Umsatz- und Neu-Kd.-Bars in Container mit zweiter Target-Line -->
                  <div class="container">
                    <span class="target-line cell-grid"
                      :style="{ width: `calc(max(12px, 12px + ((100% - 24px) * ${progressFraction})))` }"
                      title="Verstrichene Zeit"></span>
                    <BarNew v-if="r.plannedValues.salesEuro > 0" class="sales-volume" mode="performanceNormal"
                      title="Gesamt" :isEuro="true" :currentValue="campaignPerf?.salesEuro?.current || 0"
                      :goalValue="r.plannedValues.salesEuro" :useLocalProgressColors="true" />
                    <BarNew v-if="r.plannedValues.salesEuroNewCustomers > 0" class="new-customer-sales"
                      mode="performanceNormal" title="Neu-Kd." :isEuro="true"
                      :currentValue="campaignPerf?.salesEuroNewCustomers?.current || 0"
                      :goalValue="r.plannedValues.salesEuroNewCustomers" :useLocalProgressColors="true" />
                    <BarNew v-if="r.plannedValues.salesNumberNewCustomers > 0" class="new-customer-sales-integer"
                      mode="performanceNormal" title="Neu-Kd. #"
                      :currentValue="campaignPerf?.salesNumberNewCustomers?.current || 0"
                      :goalValue="r.plannedValues.salesNumberNewCustomers" :useLocalProgressColors="true" />
                  </div>
                </div>
                <div v-else class="award-note">{{ r.awardNote }}</div>
              </div>
              <div class="commission-col">
                <div class="prov" v-if="r.commission && !r.isSpecial">
                  <span class="lbl">Provision</span>
                  <span class="val">{{ formatEuro(r.commission) }}</span>
                </div>
                <div class="prov on-top" v-if="r.commissionOnTop && !r.isSpecial">
                  <span class="lbl">On Top</span>
                  <span class="val plus">+{{ formatEuro(r.commissionOnTop) }}</span>
                </div>
                <div class="prov level" v-if="r.levelValue && !r.isSpecial">
                  <span class="lbl">Punkt</span>
                  <span class="val">{{ r.levelValue.toLocaleString('de-DE') }}€</span>
                </div>
                <div class="prov special" v-if="r.isSpecial">
                  <span class="lbl">Auszeichnung</span>
                  <span class="val star">⭐</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <aside class="summary-col rank-analysis">
          <div class="super-tile analytics">
            <header>
              <h3>Rank Analyse</h3>
              <p class="desc">Verteilung & Effizienz</p>
            </header>
            <section class="analytics-block">
              <ul class="kv">
                <li><span>Gesamt</span><strong>{{ rows.length }}</strong></li>
                <li><span>Erreicht</span><strong>{{rows.filter(r => r.isPast).length}}</strong></li>
                <li><span>Ausstehend</span><strong>{{rows.filter(r => r.isFuture).length}}</strong></li>
                <li v-if="nextStatus"><span>Nächster Umsatz</span><strong>{{
                  nextStatus.plannedValues.salesEuro?.toLocaleString('de-DE') }}€</strong></li>
              </ul>
            </section>
            <section class="focus-hint" v-if="focusPath && plannedStatus">
              <p>Pfad (Aktuell → Geplant): <strong>{{ currentStatusName }}</strong> → <strong>{{ plannedStatus.name
                  }}</strong></p>
              <button class="btn ghost small" @click.prevent="focusPath = false">Fokus lösen</button>
            </section>
            <section class="export-block">
              <button class="btn primary w-full" @click.prevent>Export CSV</button>
            </section>
          </div>
        </aside>
      </div>
    </div>
  </div>

  <reminder />
</template>

<style lang="scss" scoped>

.performance-ranks {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  padding: 6.25rem 0 5rem;
}

.performance-ranks .page-inner {
  padding-inline: 1.5rem;
  width: 100%;
  box-sizing: border-box;
}

.frame-width {
  max-width: 1500px;
  margin: 0 auto;
}

.hero {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 2.25rem 1.5rem 1.75rem;
  margin-top: 1rem;
  background: linear-gradient(135deg, rgba(249, 115, 22, .08), rgba(249, 115, 22, .02));
  border: 1px solid rgba(249, 115, 22, .25);
  border-radius: 28px;
  position: relative;
  overflow: hidden;
}

.hero:before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 75% 25%, rgba(249, 115, 22, .25), transparent 60%);
  opacity: .6;
  pointer-events: none;
}

/* Back button now outside hero */
.top-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: .75rem;
}

.back-btn {
  font-size: .6rem;
  font-weight: 600;
  text-decoration: none;
  background: #fff;
  border: 1px solid #e2e8f0;
  padding: .4rem .65rem;
  border-radius: 8px;
  color: #334155;
  display: inline-flex;
  gap: .35rem;
  align-items: center;
  letter-spacing: .05em;
}

.back-btn:hover {
  background: #f1f5f9;
}

.theme-dark .back-btn {
  background: #1e293b;
  border-color: #334155;
  color: #e2e8f0;
}

.theme-dark .back-btn:hover {
  background: #334155;
}

.hero-main {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 2rem;
  justify-content: space-between;
  position: relative;
  z-index: 2;
}

.hero-titles h1 {
  margin: 0 0 .4rem;
  font-size: 2.05rem;
  letter-spacing: .5px;
}

.hero-titles .subtitle {
  margin: 0;
  font-size: .85rem;
  opacity: .75;
}

.hero-stats {
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
}

.hero-stats .stat {
  display: flex;
  flex-direction: column;
  gap: .25rem;
  min-width: 110px;
}

.hero-stats .stat .label {
  font-size: .55rem;
  text-transform: uppercase;
  letter-spacing: .09em;
  opacity: .65;
  font-weight: 600;
}

.hero-stats .stat .value {
  font-size: .9rem;
  font-weight: 600;
}

.hero-stats .stat .value.accent {
  color: var(--sellwerk-color-accent-main, #f97316);
}

.hero-stats .stat .value.planned {
  color: #6366f1;
}

.hero-stats .stat .value.next {
  color: #0ea5e9;
}

.hero-stats .stat .value.prov {
  color: var(--sellwerk-color-success, #16a34a);
}

.toggle-group {
  display: flex;
  gap: .5rem;
  flex-wrap: wrap;
}

.pill {
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: .45rem .85rem;
  font-size: .6rem;
  font-weight: 600;
  letter-spacing: .05em;
  text-transform: uppercase;
  cursor: pointer;
  border-radius: 8px;
  color: #334155;
  transition: .18s;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: .35rem;
}

.pill:hover {
  background: #f1f5f9;
}

.pill.active {
  background: var(--sellwerk-color-accent-main, #f97316);
  border-color: var(--sellwerk-color-accent-main, #f97316);
  color: #fff;
  box-shadow: 0 0 0 1px var(--sellwerk-color-accent-main, #f97316) inset, 0 4px 10px -4px rgba(249, 115, 22, .5);
}

.pill.active:hover {
  filter: brightness(1.05);
}

.theme-dark .pill {
  background: #1e293b;
  border-color: #334155;
  color: #e2e8f0;
}

.theme-dark .pill:hover {
  background: #334155;
}

.theme-dark .pill.active {
  background: var(--sellwerk-color-accent-main, #f97316);
  border-color: var(--sellwerk-color-accent-main, #f97316);
  color: #fff;
}

.hero-secondary {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: .65rem;
}

.hero-secondary h3 {
  margin: 0;
  font-size: .8rem;
  letter-spacing: .08em;
  text-transform: uppercase;
  opacity: .75;
}

.delta-inline {
  list-style: none;
  display: flex;
  gap: 1.25rem;
  padding: 0;
  margin: 0;
  flex-wrap: wrap;
}

.delta-inline li {
  display: flex;
  flex-direction: column;
  gap: .15rem;
  font-size: .7rem;
}

.delta-inline li strong {
  font-size: .85rem;
  font-weight: 600;
}

.theme-dark .performance-ranks .page-head {
  --sp-head-bg: #0f172a;
  border-color: #1e293b;
}

.headline-wrapper h2 {
  margin: 0;
  font-size: 1.4rem;
}

.headline-wrapper .subline {
  font-size: .75rem;
  opacity: .75;
}

.empty {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
}

.target-status-grid-row {
  grid-column: 1 / -1;
}

.layout-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 2.25rem;
  align-items: start;
  position: relative;
}

.list-col {
  min-width: 0;
}

.rows-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  // max-width: 880px;
  position: relative;
  margin-top: 1.25rem;
}

.timeline-spine {
  position: absolute;
  left: 90px;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(to bottom, var(--sellwerk-color-accent-main, #f97316), rgba(249, 115, 22, .15));
  border-radius: 2px;
  opacity: .35;
}

.summary-col {
  align-self: start;
}

.rank-analysis {
  margin-top: 1.25rem;
}

.super-tile {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 1.1rem 1.1rem 1.4rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  box-shadow: 0 2px 4px -2px rgba(0, 0, 0, .05), 0 8px 16px -6px rgba(0, 0, 0, .06);
}

.super-tile.analytics header h3 {
  font-size: 1rem;
}

.super-tile.analytics .kv {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .65rem .75rem;
  font-size: .65rem;
}

.super-tile.analytics .kv li {
  display: flex;
  justify-content: space-between;
  gap: .5rem;
}

.super-tile.analytics .kv strong {
  font-weight: 600;
}

.focus-hint {
  font-size: .65rem;
  display: flex;
  flex-direction: column;
  gap: .5rem;
}

.btn.small {
  font-size: .55rem;
  padding: .35rem .55rem;
}

.w-full {
  width: 100%;
}

.super-tile header h3 {
  margin: 0 0 .15rem;
  font-size: 1.05rem;
}

.super-tile header .desc {
  margin: 0;
  font-size: .7rem;
  opacity: .7;
}

.status-block,
.delta-block,
.commission-block-agg,
.actions-block {
  display: flex;
  flex-direction: column;
  gap: .5rem;
}

.status-block .status-line {
  display: flex;
  justify-content: space-between;
  font-size: .75rem;
}

.status-block .value {
  font-weight: 600;
}

.status-block .value.accent {
  color: var(--sellwerk-color-accent-main, #f97316);
}

.status-block .value.planned {
  color: #6366f1;
}

.status-block .value.next {
  color: #0ea5e9;
}

.delta-block h4,
.commission-block-agg h4 {
  margin: 0 0 .25rem;
  font-size: .72rem;
  letter-spacing: .08em;
  text-transform: uppercase;
  opacity: .7;
}

.delta-list {
  margin: 0;
  padding-left: 1.05rem;
  display: flex;
  flex-direction: column;
  gap: .15rem;
  font-size: .7rem;
}

.delta-list li strong {
  font-weight: 600;
}

.commission-block-agg .big-val {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.commission-block-agg .on-top {
  font-size: .65rem;
  font-weight: 600;
  color: var(--sellwerk-color-success, #16a34a);
}

.actions-block {
  flex-direction: row;
  gap: .5rem;
}

.btn {
  font-size: .65rem;
  border: 1px solid #e2e8f0;
  padding: .45rem .75rem;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.btn.primary {
  background: var(--sellwerk-color-accent-main, #f97316);
  color: #fff;
  border-color: var(--sellwerk-color-accent-main, #f97316);
}

.btn.ghost {
  background: transparent;
}

.toggle {
  display: flex;
  align-items: center;
  gap: .35rem;
  font-size: .6rem;
  text-transform: uppercase;
  letter-spacing: .08em;
  font-weight: 600;
}

.toggle input {
  transform: translateY(1px);
}

.rank-row {
  display: grid;
  grid-template-columns: 210px minmax(0, 1fr) 160px;
  gap: .9rem;
  align-items: stretch;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: .85rem .95rem .95rem;
  position: relative;
  overflow: hidden;
}

.rank-row .container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: calc(12px * 2);
}

.rank-row:before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, rgba(249, 115, 22, .07), transparent 65%);
  opacity: 0;
  transition: .35s ease;
}

.rank-row.current:before {
  opacity: 1;
}

.rank-row.current {
  border-color: var(--sellwerk-color-accent-main, #f97316);
  box-shadow: 0 4px 20px -6px rgba(249, 115, 22, .35), 0 0 0 1px var(--sellwerk-color-accent-main, #f97316) inset;
}

.rank-row.achieved {
  border-color: var(--sellwerk-color-success, #16a34a);
}

.rank-row.star-seller {
  border-color: #6366f1;
  background: linear-gradient(135deg, #eef2ff, #ecfeff);
  box-shadow: 0 0 0 1px #6366f1 inset, 0 4px 16px -4px rgba(99, 102, 241, .35);
}

.rank-row.future .avatar-col,
.rank-row.future .bars-col,
.rank-row.future .commission-col {
  opacity: .55;
  transition: .3s;
}

/* Planned target should retain full prominence */
.rank-row.planned .avatar-col,
.rank-row.planned .bars-col,
.rank-row.planned .commission-col {
  opacity: 1 !important;
}

/* focus path now filters rows entirely; opacity helpers removed */
.rank-row.past {
  opacity: 1;
}

/* dynamic state classes based on logic */
/* state helpers intentionally minimal; opacity set above */
.avatar-col {
  display: flex;
  gap: .65rem;
  align-items: center;
}

.avatar-col .labels {
  display: flex;
  flex-direction: column;
  gap: .25rem;
}

.rank-name {
  margin: 0;
  font-size: .8rem;
  font-weight: 600;
}

.tag {
  font-size: .55rem;
  letter-spacing: .08em;
  text-transform: uppercase;
  font-weight: 700;
  background: #e2e8f0;
  padding: .2rem .4rem;
  border-radius: 4px;
  width: fit-content;
}

.tag.planned {
  background: #fde68a;
}

.tag.award {
  background: linear-gradient(90deg, #6366f1, #0ea5e9);
  color: #fff;
}

.planned-target {
  position: relative;
}

.planned-target:after {
  content: "?";
  font-weight: 700;
  margin-left: .25rem;
  color: #6366f1;
}

.rank-row.current .tag {
  background: #2563eb;
  color: #fff;
}

.rank-row.achieved .tag {
  background: var(--sellwerk-color-success, #16a34a);
  color: #fff;
}

.tag.past {
  background: var(--sellwerk-color-success, #16a34a);
  color: #fff;
}

.bars-col {
  display: flex;
  flex-direction: column;
  gap: .55rem;
}

.bars-col {
  display: flex;
  flex-direction: column;
  gap: .55rem;
}

.bars-col .bars-wrapper {
  // padding: .25rem 0;
  max-width: 540px;
}

.tag-current {
  background: var(--sellwerk-color-accent-main, #f97316) !important;
  color: #fff;
}

.rank-row.current .tag-current {
  background: var(--sellwerk-color-accent-main, #f97316) !important;
}

.bars-col .bars-wrapper {
  // padding: .25rem 0;
}

.commission-col {
  display: flex;
  flex-direction: column;
  gap: .4rem;
  justify-content: center;
}

.prov {
  display: flex;
  flex-direction: column;
  gap: .15rem;
}

.prov .lbl {
  font-size: .55rem;
  letter-spacing: .08em;
  text-transform: uppercase;
  opacity: .6;
  font-weight: 700;
}

.prov .val {
  font-size: .75rem;
  font-weight: 700;
}

.prov.on-top .val.plus {
  color: #15803d;
}

.prov.level .val {
  color: #7c3aed;
}

.prov.special .val.star {
  font-size: 1.1rem;
  line-height: 1;
}

.award-note {
  font-size: .65rem;
  font-weight: 600;
  padding: .5rem .65rem;
  background: linear-gradient(90deg, #eef2ff, #ecfeff);
  border: 1px dashed #6366f1;
  border-radius: 8px;
  color: #312e81;
}

@media (max-width: 1500px) {
  .frame-width {
    max-width: 1380px;
  }
}

@media (max-width: 1380px) {
  .frame-width {
    max-width: 1260px;
  }
}

@media (max-width: 1280px) {
  .layout-grid {
    grid-template-columns: 1fr 340px;
  }
}

@media (max-width: 1100px) {
  .layout-grid {
    grid-template-columns: 1fr;
  }

  .summary-col {
    position: relative;
    top: 0;
  }
}

@media (max-width: 980px) {
  .rank-row {
    grid-template-columns: 180px 1fr;
  }

  .commission-col {
    flex-direction: row;
    flex-wrap: wrap;
    gap: .75rem;
    grid-column: 1 / -1;
    padding-top: .4rem;
  }

  .commission-col .prov {
    min-width: 110px;
  }
}

.performance-ranks.compact .rank-row {
  grid-template-columns: 180px minmax(0, 1fr) 120px;
  padding: .55rem .65rem;
  border-radius: 14px;
}

.performance-ranks.compact .avatar-col .rank-name {
  font-size: .7rem;
}

.performance-ranks.compact .bars-col .bars-wrapper {
  max-width: 420px;
}

.performance-ranks.compact .prov .val {
  font-size: .65rem;
}

.theme-dark .rank-row {
  background: #0f172a;
  border-color: #1e293b;
}

.theme-dark .empty {
  background: #0f172a;
  border-color: #1e293b;
}

.theme-dark .tag {
  background: #334155;
  color: #e2e8f0;
}

.theme-dark .tag.planned {
  background: #b45309;
  color: #fff;
}

.theme-dark .prov .lbl {
  opacity: .75;
}
</style>
